/**
 * @file lib/embedding/vector-store.ts
 * @description usearchを使用したベクトルストアの実装
 * @author GFTD Co., Ltd.
 */

import { Index } from 'usearch';
import { Document } from '@langchain/core/documents';
import LightningFS from '@isomorphic-git/lightning-fs';
import { randomUUID } from 'crypto';

/**
 * ベクトルストアエントリー
 */
export interface VectorStoreEntry {
  id: string;
  vector: number[];
  document: Document;
  timestamp: number;
}

/**
 * 検索結果
 */
export interface SearchResult {
  document: Document;
  score: number;
  id: string;
}

/**
 * VectorStoreクラス
 * usearchを使用した高速ベクトル検索
 */
export class VectorStore {
  private index: Index;
  private fs: LightningFS;
  private entries: Map<string, VectorStoreEntry>;
  private dimensions: number;
  private isInitialized = false;

  constructor(fs: LightningFS, dimensions: number = 384) {
    this.fs = fs;
    this.dimensions = dimensions;
    this.entries = new Map();
    
    // usearchインデックスの初期化（基本的なコンストラクタ）
    this.index = new Index(this.dimensions);
  }

  /**
   * ベクトルストアの初期化
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) {
      return;
    }

    try {
      // ファイルシステムの初期化
      await this.initializeFileSystem();
      
      this.isInitialized = true;
    } catch (error) {
      console.error('Failed to initialize VectorStore:', error);
      throw error;
    }
  }

  /**
   * ファイルシステムの初期化
   */
  private async initializeFileSystem(): Promise<void> {
    try {
      const dirs = ['vectors', 'metadata'];
      
      for (const dir of dirs) {
        try {
          await this.fs.promises.mkdir(`/${dir}`);
        } catch (error) {
          // ディレクトリが既に存在する場合は無視
          if ((error as any).code !== 'EEXIST') {
            throw error;
          }
        }
      }
    } catch (error) {
      console.error('Failed to initialize file system:', error);
      throw error;
    }
  }

  /**
   * ベクトルとドキュメントを追加
   * @param vector ベクトル
   * @param document ドキュメント
   * @returns エントリーID
   */
  async add(vector: number[], document: Document): Promise<string> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const id = randomUUID();
      const entry: VectorStoreEntry = {
        id,
        vector,
        document,
        timestamp: Date.now(),
      };

      // usearchインデックスに追加
      this.index.add(BigInt(this.entries.size), new Float32Array(vector));

      // エントリーを保存
      this.entries.set(id, entry);

      // ファイルシステムにメタデータを保存
      await this.saveEntry(entry);

      return id;
    } catch (error) {
      console.error('Failed to add vector:', error);
      throw error;
    }
  }

  /**
   * ベクトル検索
   * @param query クエリベクトル
   * @param k 返す結果数
   * @returns 検索結果
   */
  async search(query: number[], k: number = 5): Promise<SearchResult[]> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const queryVector = new Float32Array(query);
      const results = this.index.search(queryVector, k);

      const searchResults: SearchResult[] = [];
      const entriesArray = Array.from(this.entries.values());

      for (let i = 0; i < results.keys.length; i++) {
        const index = Number(results.keys[i]);
        const score = results.distances[i];

        if (index < entriesArray.length) {
          const entry = entriesArray[index];
          searchResults.push({
            document: entry.document,
            score: 1 - score, // cosine similarityに変換
            id: entry.id,
          });
        }
      }

      return searchResults;
    } catch (error) {
      console.error('Failed to search vectors:', error);
      throw error;
    }
  }

  /**
   * エントリーをファイルシステムに保存
   */
  private async saveEntry(entry: VectorStoreEntry): Promise<void> {
    try {
      const filename = `/metadata/${entry.id}.json`;
      const data = JSON.stringify(entry, null, 2);
      
      await this.fs.promises.writeFile(filename, data, 'utf8');
    } catch (error) {
      console.error('Failed to save entry:', error);
      throw error;
    }
  }

  /**
   * インデックスをファイルに保存
   */
  async save(filename: string): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      // usearchインデックスを保存
      this.index.save(filename);

      // エントリーメタデータを保存
      const entriesData = JSON.stringify(Array.from(this.entries.entries()), null, 2);
      const entriesFilename = filename.replace('.bin', '_entries.json');
      
      await this.fs.promises.writeFile(entriesFilename, entriesData, 'utf8');
    } catch (error) {
      console.error('Failed to save index:', error);
      throw error;
    }
  }

  /**
   * インデックスをファイルから読み込み
   */
  async load(filename: string): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      // usearchインデックスを読み込み
      this.index.load(filename);

      // エントリーメタデータを読み込み
      const entriesFilename = filename.replace('.bin', '_entries.json');
      
      try {
        const entriesData = await this.fs.promises.readFile(entriesFilename, 'utf8');
        const entriesArray = JSON.parse(entriesData);
        
        this.entries.clear();
        for (const [id, entry] of entriesArray) {
          this.entries.set(id, entry);
        }
      } catch (error) {
        console.warn('Failed to load entries metadata:', error);
      }
    } catch (error) {
      console.error('Failed to load index:', error);
      throw error;
    }
  }

  /**
   * インデックスサイズを取得
   */
  size(): number {
    return this.index.size();
  }

  /**
   * エントリーをIDで取得
   */
  getEntry(id: string): VectorStoreEntry | undefined {
    return this.entries.get(id);
  }

  /**
   * エントリーをIDで削除
   */
  async remove(id: string): Promise<boolean> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      const entry = this.entries.get(id);
      if (!entry) {
        return false;
      }

      // エントリーを削除
      this.entries.delete(id);

      // ファイルシステムからメタデータを削除
      try {
        await this.fs.promises.unlink(`/metadata/${id}.json`);
      } catch (error) {
        console.warn('Failed to remove entry metadata:', error);
      }

      return true;
    } catch (error) {
      console.error('Failed to remove entry:', error);
      throw error;
    }
  }

  /**
   * すべてのエントリーをクリア
   */
  async clear(): Promise<void> {
    if (!this.isInitialized) {
      await this.initialize();
    }

    try {
      // インデックスをクリア
      this.index = new Index(this.dimensions);

      // エントリーをクリア
      this.entries.clear();

      // ファイルシステムのメタデータをクリア
      try {
        const files = await this.fs.promises.readdir('/metadata');
        for (const file of files) {
          await this.fs.promises.unlink(`/metadata/${file}`);
        }
      } catch (error) {
        console.warn('Failed to clear metadata files:', error);
      }
    } catch (error) {
      console.error('Failed to clear vector store:', error);
      throw error;
    }
  }

  /**
   * リソースのクリーンアップ
   */
  async cleanup(): Promise<void> {
    try {
      // 特に必要なクリーンアップ処理はないが、
      // 将来的にリソースの解放が必要な場合のために用意
      this.isInitialized = false;
    } catch (error) {
      console.error('Failed to cleanup VectorStore:', error);
      throw error;
    }
  }
}

export default VectorStore; 